/** @format */

import { createContext, useContext, useState, useEffect } from 'react';
import { ICreateHolidayProps } from '../types/holiday';
import {
  createNewHoliday,
  deleteHoliday,
  fetchHolidayList,
  updateHoliday,
} from '../Apis/holiday';
import { useSnackbar } from './SnackbarProvider';
import { getErrorMessageFromErrorObj } from '../utils/api';
const today = new Date().toISOString().split('T')[0];
const maxEndDate = new Date(today);
maxEndDate.setDate(maxEndDate.getDate() + 1);

export interface IHolidayNameListProps {
  id: number | string;
  name: string;
  holidayType: string;
  startDate: string;
  endDate: string;
  createdBy: string;
}

export const holidayObj: ICreateHolidayProps = {
  name: '',
  startDateTime: today,
  endDateTime: maxEndDate.toISOString().split('T')[0],
  holidayType: '',
  // holidayTimeline: "",
};

export interface IHoliayListStateProps {
  loading: boolean;
  records: ICreateHolidayProps[];
}

export interface HolidayNameContextType {
  search: string;
  isAdd: boolean;
  holidayState: ICreateHolidayProps;
  holidayListState: IHoliayListStateProps;
  selectedHoliday: ICreateHolidayProps | null;
  setSelectedHoliday: React.Dispatch<
    React.SetStateAction<ICreateHolidayProps | null>
  >;
  setHolidayState: React.Dispatch<React.SetStateAction<ICreateHolidayProps>>;
  toggleAddHolidayNameDrawer: () => void;
  onAddHolidaySubmit: () => void;
  onUpdateHolidaySubmit: (updatedHoliay: ICreateHolidayProps) => void;
  onDeleteHoliday: () => void;
  handleAddHolidayChange: (
    name: keyof ICreateHolidayProps,
    value: string | Date
  ) => void;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
}

export const HolidayNameContext = createContext<HolidayNameContextType | null>(
  null
);

const HolidayNameProvider = ({ children }: any) => {
  const { setSnackbar } = useSnackbar();
  const [search, setSearch] = useState<string>('');
  const [isAdd, setIsAdd] = useState<boolean>(false);
  const [selectedHoliday, setSelectedHoliday] =
    useState<ICreateHolidayProps | null>(null);
  const [holidayState, setHolidayState] = useState<ICreateHolidayProps>({
    ...holidayObj,
  });
  const [holidayListState, setHolidayListState] =
    useState<IHoliayListStateProps>({
      loading: false,
      records: [],
    });

  const toggleAddHolidayNameDrawer = () => {
    setIsAdd(!isAdd);
  };

  const onAddHolidaySubmit = async () => {
    try {
      await createNewHoliday(holidayState);
      toggleAddHolidayNameDrawer();
      setSnackbar('Holiday  Added');
      getHolidayList();
    } catch (error) {
      setSnackbar(
        getErrorMessageFromErrorObj(error, 'Failed to add Holiday '),
        'error'
      );
    }
  };

  const onUpdateHolidaySubmit = async (
    updatedHolidayState: ICreateHolidayProps
  ) => {
    try {
      await updateHoliday(updatedHolidayState);
      toggleAddHolidayNameDrawer();
      setSelectedHoliday(null);
      setSnackbar('Holiday  Updated');
      getHolidayList();
    } catch (error) {
      setSnackbar(
        getErrorMessageFromErrorObj(error, 'Failed to update Holiday '),
        'error'
      );
    }
  };

  const onDeleteHoliday = async () => {
    try {
      if (!selectedHoliday) return;
      await deleteHoliday(selectedHoliday.id);
      setSelectedHoliday(null);
      setSnackbar('Holiday  deleted');
      getHolidayList();
    } catch (error) {
      setSnackbar(
        getErrorMessageFromErrorObj(error, 'Failed to dalete Holiday'),
        'error'
      );
    }
  };

  const handleAddHolidayChange = (
    name: keyof ICreateHolidayProps,
    value: string | Date
  ) => {
    setHolidayState({ ...holidayState, [name]: value });
  };

  const updateHolidayListState = (
    key: keyof IHoliayListStateProps,
    value: boolean | ICreateHolidayProps[]
  ) => {
    setHolidayListState({ ...holidayListState, [key]: value });
  };

  const getHolidayList = async () => {
    updateHolidayListState('loading', true);
    try {
      const response = await fetchHolidayList();
      updateHolidayListState('loading', false);
      updateHolidayListState('records', response.records);
    } catch (error) {
      updateHolidayListState('loading', false);
    }
  };

  useEffect(() => {
    getHolidayList();
  }, []);

  return (
    <HolidayNameContext.Provider
      value={{
        search,
        isAdd,
        holidayState,
        holidayListState,
        selectedHoliday,
        setSelectedHoliday,
        setHolidayState,
        handleAddHolidayChange,
        onAddHolidaySubmit,
        onUpdateHolidaySubmit,
        onDeleteHoliday,
        toggleAddHolidayNameDrawer,
        setSearch,
      }}
    >
      {children}
    </HolidayNameContext.Provider>
  );
};

export const useHolidayName = () =>
  useContext(HolidayNameContext) as HolidayNameContextType;

export default HolidayNameProvider;
